import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { RENEWAL_CONFIRMATION, RENEWAL_ERROR } from 'constants/routeConstants';
import { setDirectDebit } from 'services/RenewalService';
import Button from 'shared/components/Button/Button';
import { PaymentError } from 'types/expiryRenewal';
import { Store } from 'types/store';
import InfoBlock from '../../common-components/InfoBlock/InfoBlock';
import InfoModal from '../../common-components/InfoModal/InfoModal';
import DirectDebit from '../../assets/svg/directdebit.svg';

import '../../expiryRenewalGlobals.scss';

interface Props {
  monthlyFlex?: boolean;
  setDirectDebitDataValid?: (_valid: boolean) => void;
  setDirectDebitEdit?: (_edit: boolean) => void;
  useCounter?: boolean;
}

interface FormState {
  Name: string;
  AccountNumber: string;
  SortCode: string;
}

const DirectDebitForm = ({
  monthlyFlex = false,
  setDirectDebitDataValid,
  setDirectDebitEdit,
  useCounter = true,
}: Props) => {
  const { customerId } = useSelector((state: Store) => state.session);
  const { pageModel, paymentData } = useSelector((state: Store) => state.expiryRenewal);
  const [validateLoading, setValidateLoading] = useState(false);
  const [formModal, setFormModal] = useState(false);
  const [formState, setFormState] = useState<FormState>({ Name: '', AccountNumber: '', SortCode: '' });
  const [disabled, setDisabled] = useState(true);
  const [error, setError] = useState<PaymentError | null>(null);
  const [count, setCount] = useState(1);

  const navigate = useNavigate();

  useEffect(() => {
    let ignoreSetState = false;

    // Form validation
    if (!ignoreSetState && formState) {
      const isEmpty = Object.values(formState).some((val) => !val);
      const isValidSortCode = /^\d{6}$/.test(formState.SortCode ?? '');
      const isValidAccountNumber = /^\d{8}$/.test(formState.AccountNumber ?? '');
      setDisabled(isEmpty || !isValidSortCode || !isValidAccountNumber);
    }

    return () => {
      ignoreSetState = true;
    };
  }, [formState]);

  const createValidator = <E extends React.SyntheticEvent>(regex: RegExp, getValue: (_e: E) => string) => (e: E) => {
    if (!regex.test(getValue(e))) {
      e.preventDefault();
    }
  };

  const validateTextEntry = createValidator<React.KeyboardEvent>(/^[a-zA-Z-' ]+$/, (e) => e.key);
  const validateNumericEntry = createValidator<React.KeyboardEvent>(/^[0-9]$/, (e) => e.key);
  const validateTextPaste = createValidator<React.ClipboardEvent>(/^[a-zA-Z-' ]+$/, (e) => e.clipboardData.getData('text'));
  const validateNumericPaste = createValidator<React.ClipboardEvent>(/^[0-9]+$/, (e) => e.clipboardData.getData('text'));

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFormState({ ...formState, [id]: value });
  }

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setValidateLoading(true);

    try {
      const payload = {
        CustomerId: customerId,
        Price: paymentData?.price,
        LockToken: paymentData?.lockToken,
        RiskVersion: pageModel?.renewalRiskVersion,
        AccountHolder: formState?.Name,
        AccountNumber: formState?.AccountNumber,
        Sortcode: formState?.SortCode,
      }

      const response = await setDirectDebit(payload, monthlyFlex);

      setValidateLoading(false);

      // Payload determines if account details are valid
      if (response?.payload) {
        const { isDirectDebitSuccessful, maxAttemptNumberReached } = response.payload;

        if (isDirectDebitSuccessful) {
          if (monthlyFlex) {
            setDirectDebitDataValid?.(true);
            setDirectDebitEdit?.(false);
            return;
          }
          navigate(RENEWAL_CONFIRMATION, { state: { isDirectDebit: true }, replace: true });
          return;
        }

        if (useCounter && (maxAttemptNumberReached || (count === 3))) {
          // Navigate to error page if member submits incorrect details on 3 occasions
          navigate(RENEWAL_ERROR, { state: { isPaymentError: true }, replace: true });
        } else {
          // Invalid account details so boolean returned to parent component to showInvalidModal
          setError({ message: 'Invalid account details.', title: 'Direct debit validate error' });
          // Increasing the failed attempts counter
          setCount(count + 1);
        }
      }
    } catch {
      setValidateLoading(false);
      setError({ message: 'Error occured while submitting data.', title: 'Request failed' });
    }
  };

  return (
    <div className="d-flex justify-content-center border border-1 border-grey rounded-2">
      <div className="vstack gap-1">
        <p className="fw-bold fs-6 p-3 m-0 lh-sm">
          Add payment details
        </p>
        <div className="bg-light mx-3 mb-3 p-3 rounded">
          <div className="mt-3 mb-4">
            <img src={DirectDebit} alt="Direct Debit" />
          </div>
          <p className="fw-bold fs-5 py-3 m-0 lh-sm">
            Your bank details
          </p>
          <Form onSubmit={handleSubmit} className="dd-form">
            <Form.Control
              id="Name"
              placeholder="Account holder’s name"
              onChange={handleInputChange}
              onKeyPress={validateTextEntry}
              onPaste={validateTextPaste}
              maxLength={50}
              type="text"
            />
            <Form.Control
              id="AccountNumber"
              placeholder="Account number"
              onChange={handleInputChange}
              onKeyPress={validateNumericEntry}
              onPaste={validateNumericPaste}
              maxLength={8}
              type="text"
              inputMode="numeric"
            />
            <Form.Control
              id="SortCode"
              placeholder="Sort code"
              onChange={handleInputChange}
              onKeyPress={validateNumericEntry}
              onPaste={validateNumericPaste}
              maxLength={6}
              type="text"
              inputMode="numeric"
            />
            <div className="vstack gap-3">
              {error?.message && error?.title && (
                <InfoBlock header={error?.title} variant="danger" showIcon>
                  <span className="fw-bold text-danger">
                    {error?.message}
                  </span>
                </InfoBlock>
              )}
              <p className="fs-5 fw-bold m-0 pt-4 pb-0 m-0 lh-sm">Payment summary</p>
              <p className="fs-6 fw-bold m-0">By clicking &apos;Continue&apos;, you confirm that:</p>
              <p className="fs-6 lh-1 m-0 mb-3">
                <b className="mx-1">&bull;</b> These details are correct and you’re the account holder.<br /><br />
                <b className="mx-1">&bull;</b> You’re authorised to set up a Direct Debit with this account.<br /><br />
                <b className="mx-1">&bull;</b> You agree to the Direct Debit instructions to your bank.<br /><br />
              </p>
            </div>
            <p className="mb-4">
              <a
                className="text-decoration-underline"
                href="#directdebitinstructions"
                onClick={(e) => {
                  e.preventDefault();
                  setFormModal(true);
                }}
              >
                Direct Debit instructions & Guarantee
              </a>
            </p>
            <InfoModal
              size="lg"
              className="payment-info text-center"
              show={formModal}
            >
              <InfoBlock header="Information" variant="info" inline showIcon />
              <div className="py-4 text-left">
                <p className="fw-bold text-center">Direct Debit Guarantee</p>
                <p><b>&bull;</b> This Guarantee is offered by all banks and building societies that accept instructions to pay Direct Debits.</p>
                <p><b>&bull;</b> If there are any changes to the amount, date or frequency of your Direct Debit RAC Financial Services will notify you 10 working days in advance of your account being debited or as otherwise agreed. If you request RAC Financial Services to collect a payment, confirmation of the amount and date will be given to you at the time of the request.</p>
                <p><b>&bull;</b> If an error is made in the payment of your Direct Debit, by RAC Financial Services or your bank or building society, you are entitled to a full and immediate refund of the amount paid from your bank or building society - If you receive a refund you are not entitled to, you must pay it back when RAC Financial Services asks you to.</p>
                <p><b>&bull;</b> You can cancel a Direct Debit at any time by simply contacting your bank or building society. Written confirmation may be required. Please also notify us.</p>
              </div>
              <Button
                onClickHandler={() => setFormModal(false)}
                buttonText="Close"
              />
            </InfoModal>
            <Button
              disabled={validateLoading || disabled}
              onClickHandler={handleSubmit}
              buttonText={validateLoading ? 'Loading' : 'Continue'}
              loading={validateLoading}
            />
          </Form>
        </div>
      </div>
    </div>
  );
}

export default DirectDebitForm;
